import type { FC, InputHTMLAttributes, ReactNode } from 'react'

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

interface FieldProps {
    label: ReactNode
    htmlFor: string
    hint?: ReactNode
    error?: string | null
    className?: string
    children: ReactNode
}

interface SwitchProps {
    id: string
    checked: boolean
    onChange: (checked: boolean) => void
    label: ReactNode
    description?: ReactNode
}

export const inputClass = 'h-11 w-full rounded-lg border border-input bg-surface px-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-primary/25 disabled:opacity-60'

export const Field: FC<FieldProps> = ({ label, htmlFor, hint, error, className, children }): ReactNode => {
    return (
        <div className={cn('space-y-1.5', className)}>
            <label htmlFor={htmlFor} className='block text-sm font-semibold text-foreground/90'>
                {label}
            </label>

            {children}

            {error ? (
                <p className='text-xs font-medium text-danger' role='alert'>
                    {error}
                </p>
            ) : hint ? (
                <p className='text-xs text-muted-foreground'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...rest }, ref) => (
    <input ref={ref} className={cn(inputClass, className)} {...rest} />
))

TextInput.displayName = 'TextInput'

export const Switch: FC<SwitchProps> = ({ id, checked, onChange, label, description }): ReactNode => {
    return (
        <div className='flex items-center justify-between gap-4 rounded-lg border border-border bg-surface px-3.5 py-3'>
            <div className='min-w-0'>
                <label htmlFor={id} className='block cursor-pointer text-sm font-semibold'>
                    {label}
                </label>

                {description && (
                    <p className='text-xs text-muted-foreground'>
                        {description}
                    </p>
                )}
            </div>

            <button
                id={id}
                type='button'
                role='switch'
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={cn(
                    'relative h-6 w-11 shrink-0 rounded-full border transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                    checked ? 'border-primary bg-primary' : 'border-input bg-secondary'
                )}
            >
                <span
                    className={cn(
                        'absolute top-0.5 left-0.5 size-[18px] rounded-full bg-white shadow transition-transform',
                        checked ? 'translate-x-5' : 'translate-x-0'
                    )}
                />
            </button>
        </div>
    )
}
